import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styles from './MaintenancesFiltersScreen.module.scss';
import Button from 'common/components/button/Button';
import FormItem from 'common/components/formItem/FormItem';
import Label from 'common/components/label/Label';
import { Col, Row } from 'react-flexbox-grid';
import { SelectValueLabel, SelectBooleanLabel } from 'common/types/SelectValueLabel';
import VehiclesService from 'api/vehicles/VehiclesService';
import Select from 'common/components/select/Select';
import { Controller, useForm } from 'react-hook-form';
import UsersService from 'api/users/UsersService';
import { UsersSelectItemDto } from 'api/users/models/UserDto';
import SuppliersService from 'api/suppliers/SuppliersService';
import { CatalogSupplierSearchCriteria } from 'api/catalog/models/CatalogSupplierSearchCriteria';
import { VehicleMaintenanceStates } from 'api/vehicleMaintenances/enums/VehicleMaintenanceStates';
import MaintenanceContractsService from 'api/contracts/maintenanceContract/MaintenanceContractsService';
import MaintenanceComponentsService from 'api/maintenanceComponents/MaintenanceComponentsService';
import LocalsService from 'api/locals/LocalsService';
import VehicleSegmentsService from 'api/vehicleSegments/VehicleSegmentsService';
import RangeInputPicker from 'common/components/rangeInputPicker/RangeInputPicker';
import ToggleSwitch from 'common/components/toggleSwitch/ToggleSwitch';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import { FaInfoCircle } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import Utils from 'common/services/Utils';

export interface Filters {
    startDate?: Date;
    endDate?: Date;
    vehicleId?: string;
    vehicleName?: string;
    typeId?: string;
    typeName?: string;
    responsibleId?: string;
    responsibleName?: string;
    supplierId?: string;
    supplierName?: string;
    currentState?: string;
    currentStateName?: string;
    executedStartDate?: Date;
    executedEndDate?: Date;
    vehiclesInactive?: boolean;
    page?: number;
    orderColumn?: string;
    orderBy?: string;
    componentIds?: string[];
    componentNames?: string[];
    localIds?: string[];
    localNames?: string[];
    localOperator?: string;
    segmentId?: string;
    segmentName?: string;
    // Bidirectional scroll support
    scrollToItemId?: string;
    // Reminder filters
    reminderPredicted?: boolean;
    reminderPredictedName?: string;
    reminderScheduling?: boolean;
    reminderSchedulingName?: string;
}

type Props = {
    filters: Filters;
    genericFilter?: boolean;
    onChange?: (filters: Filters) => void;
    onFilter: (filters: Filters) => void;
}

const MaintenancesFiltersScreen: React.FC<Props> = ({ filters, genericFilter = false, onChange, onFilter }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const { vehicleId, month } = useParams<{ vehicleId: string, month: string }>();

    const form = useForm<Filters>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });
    const { setValue, getValues, watch } = form;
    const [typesOptions, setTypes] = useState<SelectValueLabel[]>([]);
    const [vehiclesOptions, setVehicles] = useState<SelectValueLabel[]>([]);
    const [responsiblesOptions, setResponsibles] = useState<SelectValueLabel[]>([]);
    const [suppliersOptions, setSuppliers] = useState<SelectValueLabel[]>([]);
    const [statesOptions, setStatesOptions] = useState<SelectValueLabel[]>([]);
    const [componentsOptions, setComponentsOptions] = useState<SelectValueLabel[]>([]);
    const [segmentsOptions, setSegments] = useState<SelectValueLabel[]>([]);
    const [localsOptions, setLocals] = useState<SelectValueLabel[]>([]);

    const [yesNoOptions] = useState<SelectBooleanLabel[]>([
        { label: t('common.yes'), value: true },
        { label: t('common.no'), value: false }
    ]);

    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    const getData = async () => {
        try {
            Loading.show();

            const [types, vehiclesOptionsList, responsiblesOptionsList, suppliersOptionsList, componentsList, segmentsOptionsDB, localsOptionsDB] = await Promise.all([
                MaintenanceContractsService.getMaintenanceContractTypes(),
                VehiclesService.catalogByMaintenances({}),
                UsersService.getAllForCompany(),
                SuppliersService.catalog({ typeName: 'MAINTENANCE' } as CatalogSupplierSearchCriteria),
                MaintenanceComponentsService.catalog(),
                VehicleSegmentsService.catalog(),
                LocalsService.catalog()
            ]);

            setTypes(Utils.sortAlphabetically(types.map(x => {
                return {
                    ...x,
                    label: x.isSystem ? t(`common.maintenance_contract_type.${x.label}` as any) : x.label
                }
            })));

            setVehicles(vehiclesOptionsList);

            const suppliersWithNone = [
                { value: null, label: t('maintenances.list.none') },
                ...Utils.sortAlphabetically(suppliersOptionsList)
            ];
            setSuppliers(suppliersWithNone);

            const responsiblesWithNone = [
                { value: null as any, label: t('maintenances.list.none') },
                ...(responsiblesOptionsList || []).map((x: UsersSelectItemDto) => ({ value: x.id || '', label: x.realName || '' }))
            ];
            setResponsibles(responsiblesWithNone);

            const states: SelectValueLabel[] = [
                { value: VehicleMaintenanceStates.PREDICTED, label: t('maintenances.states.PREDICTED') },
                { value: VehicleMaintenanceStates.PERFORM, label: t('maintenances.states.PERFORM') }];

            setStatesOptions(states);

            setComponentsOptions(Utils.sortAlphabetically(componentsList));

            setSegments(Utils.sortAlphabetically(segmentsOptionsDB));
            setLocals([{ value: '00000000-0000-0000-0000-000000000000', label: t('maintenances.list.none') as string }, ...Utils.sortAlphabetically(localsOptionsDB)]);

            if (filters.startDate) {
                form.setValue('startDate', new Date(filters.startDate));
            }

            if (filters.endDate) {
                form.setValue('endDate', new Date(filters.endDate));
            }

            if (filters.executedStartDate) {
                form.setValue('executedStartDate', new Date(filters.executedStartDate));
            }

            if (filters.executedEndDate) {
                form.setValue('executedEndDate', new Date(filters.executedEndDate));
            }

            setIsLoaded(true);

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get maintenances filter`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
        finally {
            Loading.hide();
        }
    }

    useEffect(() => {
        getData();
    }, []);
    useEffect(() => {
        ReactTooltip.rebuild()
    }, []);
    const clearFilters = () => {
        form.reset({
            startDate: undefined,
            endDate: undefined,
            responsibleId: undefined,
            responsibleName: undefined,
            vehicleId: vehicleId,
            vehicleName: undefined,
            typeId: undefined,
            typeName: undefined,
            supplierId: undefined,
            supplierName: undefined,
            currentState: undefined,
            currentStateName: undefined,
            executedStartDate: undefined,
            executedEndDate: undefined,
            vehiclesInactive: false,
            orderColumn: 'vm.maintenance_date',
            orderBy: 'desc',
            componentIds: undefined,
            componentNames: undefined,
            localIds: [],
            localNames: [],
            localOperator: 'OR',
            segmentId: undefined,
            segmentName: undefined,
            reminderPredicted: undefined,
            reminderPredictedName: undefined,
            reminderScheduling: undefined,
            reminderSchedulingName: undefined,
        });
        onInputChange();
        onSubmit();
    }

    const onSubmit = () => {
        onFilter(getValues());
    }

    const onInputChange = () => {
        if (onChange) {
            onChange(form.getValues());
        }
    }

    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.formContent}>
                {isLoaded && <div>
                    <FormItem>
                        {genericFilter && <Row>
                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.vehicle')}
                                        <FaInfoCircle data-tip={t('maintenances.only_vehicles_with_maintenances')} style={{ marginLeft: '0.5rem' }} />
                                    </Label>
                                    <ReactTooltip />
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={vehiclesOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.vehicle')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('vehicleName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={vehiclesOptions.find(x => x.value === value) ?? null}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="vehicleId"
                                        defaultValue={getValues('vehicleId')} />
                                </FormItem>

                            </Col>
                        </Row>}

                        <Row>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.type')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={typesOptions}
                                                    isClearable
                                                    placeholder={t('common.type')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('typeName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={typesOptions.find(x => x.value === value) ? { label: typesOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="typeId"
                                        defaultValue={form.getValues('typeId')} />
                                </FormItem>
                            </Col>

                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.state')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={statesOptions}
                                                    isClearable
                                                    placeholder={t('common.state')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('currentStateName', data?.label ?? null);
                                                        onInputChange();

                                                        if (data && data.value == VehicleMaintenanceStates.PREDICTED) {
                                                            form.setValue('executedStartDate', undefined);
                                                            form.setValue('executedEndDate', undefined);
                                                        }

                                                        if (data && data.value == VehicleMaintenanceStates.PERFORM) {
                                                            form.setValue('startDate', undefined);
                                                            form.setValue('endDate', undefined);
                                                        }

                                                    }}
                                                    value={statesOptions.find(x => x.value === value) ? { label: statesOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                    isDisabled={form.watch('startDate') != null || form.watch('endDate') != null || form.watch('executedStartDate') != null || form.watch('executedEndDate') != null}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="currentState"
                                        defaultValue={form.getValues('currentState')} />
                                </FormItem>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('maintenances.list.components')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={componentsOptions}
                                                    isMulti
                                                    isClearable
                                                    placeholder={t('maintenances.list.components')}
                                                    onChange={(data: SelectValueLabel[]) => {
                                                        onSelectChange(data?.map(d => d.value) ?? []);
                                                        setValue('componentNames', data?.map(d => d.label) ?? []);
                                                        onInputChange();
                                                    }}
                                                    value={componentsOptions.filter(x => (value || []).includes(x.value))}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="componentIds"
                                        defaultValue={getValues('componentIds') || []}
                                    />
                                </FormItem>
                            </Col>
                        </Row>

                        {genericFilter && <Row>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.type')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={segmentsOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.type')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('segmentName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={segmentsOptions.find(x => x.value === value) ? { label: segmentsOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="segmentId"
                                        defaultValue={getValues('segmentId')} />
                                </FormItem>
                            </Col>

                            <Col xs={6}>
                                <FormItem>
                                    <div className={styles.labelWithOperator}>
                                        <Label className={styles.bold}>{t('vehicle.local')}</Label>
                                        <div className={styles.operatorToggle}>
                                            <span title={t('common.filter_all_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_all' as any) as string}
                                                    className={styles.firstChild}
                                                    preset={watch('localOperator') === 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'AND'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                            <span title={t('common.filter_any_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_any' as any) as string}
                                                    className={styles.lastChild}
                                                    preset={watch('localOperator') !== 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'OR'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                        </div>
                                    </div>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={localsOptions}
                                                    isMulti
                                                    isClearable
                                                    placeholder={t('vehicle.local')}
                                                    onChange={(data: SelectValueLabel[]) => {
                                                        onSelectChange(data?.map(d => d.value) ?? []);
                                                        setValue('localNames', data?.map(d => d.label) ?? []);
                                                        onInputChange();
                                                    }}
                                                    value={localsOptions.filter(x => (value || []).includes(x.value))}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="localIds"
                                        defaultValue={getValues('localIds') || []} />
                                </FormItem>
                            </Col>
                        </Row>}

                        <Row>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.assigned_to')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={responsiblesOptions}
                                                    isClearable
                                                    placeholder={t('common.assigned_to')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('responsibleName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={responsiblesOptions.find(x => x.value === value) ?? null}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="responsibleId"
                                        defaultValue={getValues('responsibleId')} />
                                </FormItem>
                            </Col>

                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.supplier')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={suppliersOptions}
                                                    isClearable
                                                    placeholder={t('common.supplier')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('supplierName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={suppliersOptions.find(x => x.value === value) ?? null}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="supplierId"
                                        defaultValue={getValues('supplierId')} />
                                </FormItem>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12}>
                                <FormItem>
                                    <RangeInputPicker
                                        label={t('maintenances.date_maintenance')}
                                        onChange={(startValue: any, endValue: any) => {

                                            form.setValue('executedStartDate', startValue ? moment(startValue).toDate() : undefined);
                                            form.setValue('executedEndDate', endValue ? moment(endValue).toDate() : undefined);

                                            if (startValue || endValue) {
                                                form.setValue('currentState', VehicleMaintenanceStates.PERFORM);
                                                form.setValue('currentStateName', t('maintenances.states.PERFORM'));    

                                            } else {
                                                form.setValue('currentState', undefined);
                                                form.setValue('currentStateName', undefined);
                                            }

                                            form.setValue('startDate', undefined);   
                                            form.setValue('endDate', undefined);

                                            onInputChange();
                                        }}
                                        defaultStartValue={form.getValues('executedStartDate')}
                                        defaultEndValue={form.getValues('executedEndDate')}
                                        isDatePicker
                                        disabled={form.watch('startDate') != null || form.watch('endDate') != null || form.watch('currentState') == VehicleMaintenanceStates.PREDICTED}
                                    />
                                </FormItem>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12}>
                                <FormItem>
                                    <RangeInputPicker
                                        label={t('maintenances.expected_date')}
                                        onChange={(startValue: any, endValue: any) => {
                                            form.setValue('startDate', startValue ? moment(startValue).toDate() : undefined);
                                            form.setValue('endDate', endValue ? moment(endValue).toDate() : undefined);

                                            form.setValue('executedStartDate', undefined);
                                            form.setValue('executedEndDate', undefined);

                                            if (startValue || endValue) {
                                                form.setValue('currentState', VehicleMaintenanceStates.PREDICTED);
                                                form.setValue('currentStateName', t('maintenances.states.PREDICTED'));
                                                onInputChange();
                                            } else {
                                                form.setValue('currentState', undefined);
                                                form.setValue('currentStateName', null);
                                            }
                                            onInputChange();
                                        }}
                                        defaultStartValue={form.getValues('startDate')}
                                        defaultEndValue={form.getValues('endDate')}
                                        isDatePicker
                                        disabled={form.watch('executedStartDate') != null || form.watch('executedEndDate') != null || form.watch('currentState') == VehicleMaintenanceStates.PERFORM}
                                    />
                                </FormItem>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold} title={t('maintenances.list.reminder_predicted_full') as string}>{t('maintenances.list.reminder_predicted')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={yesNoOptions}
                                                    isClearable
                                                    placeholder={t('common.all')}
                                                    onChange={(data: SelectBooleanLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('reminderPredictedName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={yesNoOptions.find(x => x.value === value) ?? null}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="reminderPredicted"
                                        defaultValue={getValues('reminderPredicted')} />
                                </FormItem>
                            </Col>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold} title={t('maintenances.list.reminder_scheduling_full') as string}>{t('maintenances.list.reminder_scheduling')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={yesNoOptions}
                                                    isClearable
                                                    placeholder={t('common.all')}
                                                    onChange={(data: SelectBooleanLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('reminderSchedulingName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={yesNoOptions.find(x => x.value === value) ?? null}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="reminderScheduling"
                                        defaultValue={getValues('reminderScheduling')} />
                                </FormItem>
                            </Col>
                        </Row>

                        {genericFilter && <Row>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('maintenances.vehicle_inactives')}</Label>
                                    <ToggleSwitch
                                        name="vehiclesInactive"
                                        form={form}
                                    />
                                </FormItem>
                            </Col>
                        </Row>}

                    </FormItem>
                </div>}
                <div className={styles.buttonsFooter}>
                    <FormItem>
                        <Button
                            text={t('common.clear')}
                            size={'normal'}
                            preset={'secondary'}
                            onClick={clearFilters}
                            type='reset' />
                        <Button
                            type='submit'
                            text={t('common.apply')}
                            size={'normal'}
                        />
                    </FormItem>
                </div>
            </div>
        </form>
    );
}

export default memo(MaintenancesFiltersScreen);